import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Navigate } from 'react-router-dom';
import { Plus, Save, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import AltVideosPanel from '@/components/AltVideosPanel';

const INSTRUMENTS = ['guitar', 'piano', 'voice', 'drums', 'bass'];
const GENRES = ['rock', 'pop', 'jazz', 'blues', 'folk', 'classical', 'electronic'];
const LEVELS = ['beginner', 'intermediate', 'advanced', 'pro'];

const emptySong = {
  title: '', artist: '', genre: 'rock', instruments: ['guitar'],
  difficulty: 'beginner', duration: '3:30', year: 2024, key: 'C', bpm: 90,
  chords: '', lyrics: '', tab: '', video_url: '', cover_url: '', description: '',
};

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState('songs');
  const [songs, setSongs] = useState([]);
  const [courses, setCourses] = useState([]);
  const [editingSong, setEditingSong] = useState(null);

  const load = async () => {
    const [s, c] = await Promise.all([api.get('/songs'), api.get('/courses')]);
    setSongs(s.data); setCourses(c.data);
  };
  useEffect(() => { load(); }, []);

  if (loading) return <Layout><div className="p-10 text-stone-400">Chargement...</div></Layout>;
  if (!user?.is_admin) return <Navigate to="/dashboard" replace />;

  const saveSong = async (data) => {
    try {
      const payload = { ...data, instruments: data.instruments, year: Number(data.year) || null, bpm: Number(data.bpm) || null };
      if (data.id) {
        await api.put(`/songs/${data.id}`, payload);
        toast.success('Chanson mise à jour');
      } else {
        await api.post('/songs', payload);
        toast.success('Chanson créée');
      }
      setEditingSong(null);
      load();
    } catch (e) { toast.error("Erreur d'enregistrement"); }
  };

  const deleteSong = async (id) => {
    if (!confirm('Supprimer cette chanson ?')) return;
    await api.delete(`/songs/${id}`);
    load();
    toast.success('Supprimée');
  };

  const deleteCourse = async (id) => {
    if (!confirm('Supprimer ce cours ?')) return;
    await api.delete(`/courses/${id}`);
    load();
    toast.success('Cours supprimé');
  };

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-6xl">
        <div className="mb-8 rise">
          <div className="overline text-orange-400 mb-2">Panneau admin</div>
          <h1 className="font-display text-5xl tracking-tight">Gérer le contenu</h1>
        </div>

        <div className="flex gap-2 mb-8 border-b border-stone-800 overflow-x-auto">
          {['songs', 'courses', 'videos', 'sotw', 'alt-videos'].map(t => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm rounded-t-lg border-b-2 transition-colors whitespace-nowrap ${
                tab === t ? 'border-orange-600 text-stone-100' : 'border-transparent text-stone-400 hover:text-stone-200'
              }`}
            >
              {t === 'songs' ? `Chansons (${songs.length})`
                : t === 'courses' ? `Cours (${courses.length})`
                : t === 'videos' ? `Vidéos de leçons`
                : t === 'sotw' ? `Chanson de la semaine`
                : `Vidéos alt.`}
            </button>
          ))}
        </div>

        {tab === 'songs' && (
          <>
            <button
              data-testid="admin-new-song"
              onClick={() => setEditingSong({ ...emptySong })}
              className="btn-primary mb-6 inline-flex items-center gap-2"
            >
              <Plus size={16} /> Nouvelle chanson
            </button>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {songs.map((s, i) => (
                <div key={s.id} data-testid={`admin-song-${i}`} className="card-stone p-4 flex items-center gap-4">
                  <div className="w-14 h-14 rounded-lg bg-stone-800 overflow-hidden shrink-0">
                    {s.cover_url && <img src={s.cover_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-xl truncate">{s.title}</div>
                    <div className="text-stone-400 text-sm truncate">{s.artist} · {s.difficulty}</div>
                  </div>
                  <button data-testid={`admin-edit-song-${i}`} onClick={() => setEditingSong(s)} className="btn-ghost text-xs">Éditer</button>
                  <button data-testid={`admin-delete-song-${i}`} onClick={() => deleteSong(s.id)} className="text-red-400 hover:text-red-300 p-2"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === 'courses' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {courses.map((c, i) => (
              <div key={c.id} className="card-stone p-4 flex items-center gap-4">
                <div className="w-14 h-14 rounded-lg bg-stone-800 overflow-hidden shrink-0">
                  {c.cover_url && <img src={c.cover_url} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-xl truncate">{c.title}</div>
                  <div className="text-stone-400 text-sm truncate">{c.instrument} · {c.level} · {c.lessons?.length || 0} leçons</div>
                </div>
                <button data-testid={`admin-delete-course-${i}`} onClick={() => deleteCourse(c.id)} className="text-red-400 hover:text-red-300 p-2"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        )}

        {tab === 'videos' && <LessonVideosPanel />}
        {tab === 'sotw' && <SongOfWeekPanel songs={songs} />}
        {tab === 'alt-videos' && <AltVideosPanel songs={songs} onSaved={load} />}
      </div>

      {editingSong && (
        <SongEditor
          initial={editingSong}
          onClose={() => setEditingSong(null)}
          onSave={saveSong}
        />
      )}
    </Layout>
  );
}

function SongEditor({ initial, onClose, onSave }) {
  const [data, setData] = useState(initial);
  const update = (k, v) => setData(d => ({ ...d, [k]: v }));

  const toggleInstrument = (inst) => {
    const list = data.instruments || [];
    update('instruments', list.includes(inst) ? list.filter(i => i !== inst) : [...list, inst]);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="glass rounded-2xl max-w-3xl w-full my-4 max-h-[calc(100vh-2rem)] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 md:px-8 md:pt-8 md:pb-4 sticky top-0 z-10 bg-stone-900/85 backdrop-blur-xl border-b border-stone-800">
          <div>
            <div className="overline text-orange-400">{data.id ? 'Éditer' : 'Nouvelle'}</div>
            <h2 className="font-display text-3xl">{data.id ? data.title : 'Nouvelle chanson'}</h2>
          </div>
          <button data-testid="admin-close-editor" onClick={onClose} className="p-2 rounded-full hover:bg-stone-800"><X size={18} /></button>
        </div>
        <div className="p-6 md:p-8 pt-4">

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Titre" testid="admin-song-title" value={data.title} onChange={v => update('title', v)} />
          <Field label="Artiste" testid="admin-song-artist" value={data.artist} onChange={v => update('artist', v)} />
          <Select label="Genre" testid="admin-song-genre" options={GENRES} value={data.genre} onChange={v => update('genre', v)} />
          <Select label="Niveau" testid="admin-song-difficulty" options={LEVELS} value={data.difficulty} onChange={v => update('difficulty', v)} />
          <Field label="Durée" testid="admin-song-duration" value={data.duration} onChange={v => update('duration', v)} />
          <Field label="Tonalité" testid="admin-song-key" value={data.key || ''} onChange={v => update('key', v)} />
          <Field label="Année" testid="admin-song-year" value={data.year || ''} onChange={v => update('year', v)} />
          <Field label="BPM" testid="admin-song-bpm" value={data.bpm || ''} onChange={v => update('bpm', v)} />
          <Field label="Video URL (embed YouTube)" testid="admin-song-video" value={data.video_url || ''} onChange={v => update('video_url', v)} full />
          <Field label="Cover URL (image)" testid="admin-song-cover" value={data.cover_url || ''} onChange={v => update('cover_url', v)} full />
        </div>

        <div className="mt-4">
          <div className="overline text-stone-400 mb-2">Instruments</div>
          <div className="flex flex-wrap gap-2">
            {INSTRUMENTS.map(i => (
              <button
                key={i}
                data-testid={`admin-song-inst-${i}`}
                onClick={() => toggleInstrument(i)}
                className={`px-3 py-1.5 rounded-full text-sm border ${
                  data.instruments?.includes(i)
                    ? 'bg-orange-700 border-orange-700 text-white'
                    : 'bg-transparent border-stone-700 text-stone-300'
                }`}
              >{i}</button>
            ))}
          </div>
        </div>

        <Textarea label="Description" testid="admin-song-desc" value={data.description || ''} onChange={v => update('description', v)} />
        <Textarea label="Accords" testid="admin-song-chords" value={data.chords || ''} onChange={v => update('chords', v)} rows={4} />
        <Textarea label="Tablature" testid="admin-song-tab" value={data.tab || ''} onChange={v => update('tab', v)} rows={4} />
        <Textarea label="Paroles" testid="admin-song-lyrics" value={data.lyrics || ''} onChange={v => update('lyrics', v)} rows={4} />

        <div className="flex gap-2 justify-end mt-6">
          <button data-testid="admin-cancel-editor" onClick={onClose} className="btn-ghost">Annuler</button>
          <button data-testid="admin-save-song" onClick={() => onSave(data)} className="btn-primary inline-flex items-center gap-2">
            <Save size={16} /> Enregistrer
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, full, testid }) {
  return (
    <label className={`block ${full ? 'md:col-span-2' : ''}`}>
      <div className="overline text-stone-400 mb-1">{label}</div>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg bg-stone-900 border border-stone-800 text-stone-100 focus:outline-none focus:border-orange-800"
      />
    </label>
  );
}

function Select({ label, options, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="overline text-stone-400 mb-1">{label}</div>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg bg-stone-900 border border-stone-800 text-stone-100 focus:outline-none focus:border-orange-800"
      >
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function Textarea({ label, value, onChange, rows = 3, testid }) {
  return (
    <label className="block mt-4">
      <div className="overline text-stone-400 mb-1">{label}</div>
      <textarea
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="w-full px-3 py-2 rounded-lg bg-stone-900 border border-stone-800 text-stone-100 focus:outline-none focus:border-orange-800 font-mono text-sm"
      />
    </label>
  );
}


// ============ LESSON VIDEOS PANEL ============
function LessonVideosPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [draftUrl, setDraftUrl] = useState('');
  const [draftLabel, setDraftLabel] = useState('Tutoriel FR');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/lesson-videos');
      setRows(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (r) => {
    setEditingKey(`${r.course_id}:${r.lesson_index}`);
    const existing = (r.override_video_urls && r.override_video_urls[0]) || (r.seed_video_urls && r.seed_video_urls[0]) || {};
    setDraftUrl(existing.url || '');
    setDraftLabel(existing.label || 'Tutoriel FR');
  };
  const save = async (r) => {
    if (!draftUrl.trim()) { toast.error('URL manquante'); return; }
    // Normalise youtube URLs to embed form
    let url = draftUrl.trim();
    const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
    if (m) url = `https://www.youtube.com/embed/${m[1]}`;
    try {
      await api.put(`/admin/courses/${r.course_id}/lessons/${r.lesson_index}/videos`, {
        video_urls: [{ url, label: draftLabel || 'Tutoriel', lang: 'fr' }],
      });
      toast.success('Vidéo mise à jour');
      setEditingKey(null);
      load();
    } catch (e) { toast.error("Erreur d'enregistrement"); }
  };
  const reset = async (r) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Restaurer la vidéo par défaut (seed) pour cette leçon ?')) return;
    try {
      await api.delete(`/admin/courses/${r.course_id}/lessons/${r.lesson_index}/videos`);
      toast.success('Retour au seed');
      load();
    } catch (e) { toast.error('Erreur'); }
  };

  if (loading) return <div className="text-stone-500 p-6">Chargement...</div>;

  // Group by course
  const grouped = rows.reduce((acc, r) => {
    (acc[r.course_id] = acc[r.course_id] || { title: r.course_title, instrument: r.instrument, level: r.level, lessons: [] }).lessons.push(r);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="admin-lesson-videos-panel">
      <div className="text-sm text-stone-400">
        Attache une vidéo YouTube vérifiée à chaque leçon. Les overrides prennent le pas sur les vidéos seed. Colle l'URL complète ou l'ID.
      </div>
      {Object.entries(grouped).map(([cid, g]) => (
        <div key={cid} className="card-stone p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-display text-xl">{g.title}</div>
              <div className="text-xs text-stone-500">{g.instrument} · {g.level}</div>
            </div>
          </div>
          <div className="divide-y divide-stone-800">
            {g.lessons.map(r => {
              const key = `${r.course_id}:${r.lesson_index}`;
              const isEditing = editingKey === key;
              const activeUrl = (r.override_video_urls[0] || r.seed_video_urls[0] || {}).url;
              return (
                <div key={key} className="py-3" data-testid={`admin-video-row-${r.lesson_index}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-stone-100 truncate">
                        L{r.lesson_index + 1}. {r.lesson_title}
                        {r.has_override && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-orange-950 text-orange-300 border border-orange-800">Override</span>}
                      </div>
                      <div className="text-xs text-stone-500 truncate font-mono">
                        {activeUrl ? activeUrl : '— aucune vidéo, fallback recherche —'}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {!isEditing && (
                        <button onClick={() => startEdit(r)} data-testid={`admin-video-edit-${r.lesson_index}`} className="btn-ghost text-xs">
                          {r.has_override ? 'Modifier' : 'Ajouter'}
                        </button>
                      )}
                      {r.has_override && !isEditing && (
                        <button onClick={() => reset(r)} data-testid={`admin-video-reset-${r.lesson_index}`} className="btn-ghost text-xs text-red-400">
                          Restaurer seed
                        </button>
                      )}
                    </div>
                  </div>
                  {isEditing && (
                    <div className="mt-3 rounded-lg border border-orange-800/50 bg-stone-900/60 p-3 space-y-2">
                      <input
                        value={draftUrl}
                        onChange={e => setDraftUrl(e.target.value)}
                        placeholder="https://www.youtube.com/watch?v=… ou juste l'ID"
                        className="w-full px-3 py-2 rounded-lg bg-stone-950 border border-stone-800 text-stone-100 text-sm font-mono"
                        data-testid={`admin-video-url-input-${r.lesson_index}`}
                      />
                      <div className="flex items-center gap-2">
                        <input
                          value={draftLabel}
                          onChange={e => setDraftLabel(e.target.value)}
                          placeholder="Label (ex: Tutoriel FR)"
                          className="flex-1 px-3 py-2 rounded-lg bg-stone-950 border border-stone-800 text-stone-100 text-sm"
                        />
                        <button onClick={() => save(r)} data-testid={`admin-video-save-${r.lesson_index}`} className="btn-primary text-xs">Enregistrer</button>
                        <button onClick={() => setEditingKey(null)} className="btn-ghost text-xs">Annuler</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ SONG OF THE WEEK PANEL ============
function SongOfWeekPanel({ songs }) {
  const [sotw, setSotw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/song-of-week');
      setSotw(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const pick = async (id) => {
    try {
      await api.post('/admin/song-of-week', { song_id: id });
      toast.success('Chanson de la semaine mise à jour');
      setPickerOpen(false);
      load();
    } catch { toast.error('Erreur'); }
  };
  const clear = async () => {
    try {
      await api.post('/admin/song-of-week', { song_id: null });
      toast.success("Sélection automatique restaurée");
      load();
    } catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="text-stone-500 p-6">Chargement...</div>;
  const song = sotw?.song;

  return (
    <div className="space-y-4" data-testid="admin-sotw-panel">
      <div className="text-sm text-stone-400">
        La chanson mise en avant sur le tableau de bord. Par défaut Maestro choisit automatiquement la plus étudiée cette semaine ; tu peux figer un choix manuel qui restera actif jusqu'au lundi suivant.
      </div>
      <div className="card-stone p-6 flex items-center gap-5 flex-wrap">
        <div className="w-24 h-24 rounded-lg bg-stone-800 overflow-hidden shrink-0">
          {song?.cover_url && <img src={song.cover_url} alt="" className="w-full h-full object-cover" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline text-orange-400 mb-1">
            {sotw?.is_manual ? 'Choix manuel' : 'Sélection automatique'}
          </div>
          <div className="font-display text-2xl truncate">{song?.title || '—'}</div>
          <div className="text-stone-400 text-sm truncate">{song?.artist} · {song?.difficulty}</div>
          <div className="text-xs text-stone-600 mt-1">Semaine du {sotw?.week_start?.slice(0, 10)}</div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setPickerOpen(v => !v)}
            data-testid="admin-sotw-change"
            className="btn-primary text-xs"
          >
            {sotw?.is_manual ? 'Changer' : 'Fixer une chanson'}
          </button>
          {sotw?.is_manual && (
            <button onClick={clear} data-testid="admin-sotw-clear" className="btn-ghost text-xs text-red-400">
              Auto
            </button>
          )}
        </div>
      </div>
      {pickerOpen && (
        <div className="card-stone p-4 max-h-80 overflow-y-auto" data-testid="admin-sotw-picker">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {songs.map(s => (
              <button
                key={s.id}
                onClick={() => pick(s.id)}
                data-testid={`admin-sotw-pick-${s.id}`}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-stone-800 text-left"
              >
                <div className="w-10 h-10 rounded bg-stone-800 overflow-hidden shrink-0">
                  {s.cover_url && <img src={s.cover_url} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="min-w-0">
                  <div className="text-stone-100 text-sm truncate">{s.title}</div>
                  <div className="text-xs text-stone-500 truncate">{s.artist}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
